import { ChatsList } from "@app/components/assistant/conversation/ChatsList";
import {
  ConversationMenu,
  useConversationMenu,
} from "@app/components/assistant/conversation/ConversationMenu";
import { CreatePodModal } from "@app/components/assistant/conversation/CreatePodModal";
import { DeleteConversationsDialog } from "@app/components/assistant/conversation/DeleteConversationsDialog";
import { InputBarContext } from "@app/components/assistant/conversation/input_bar/InputBarContext";
import { renderPodsList } from "@app/components/assistant/conversation/sidebar/PodList";
import { SidebarSearch } from "@app/components/assistant/conversation/sidebar/SidebarSearch";
import {
  filterReadTriggeredConversations,
  orderSidebarProjects,
} from "@app/components/assistant/conversation/utils";
import type { NavigationPanelSection } from "@app/components/navigation/DesktopNavigationContext";
import { useNavigationPanelSection } from "@app/components/navigation/NavigationRail";
import { ContactsList } from "@app/components/people/ContactsList";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import {
  useConversations,
  usePodConversationsSummary,
} from "@app/hooks/conversations";
import { useActiveConversationId } from "@app/hooks/useActiveConversationId";
import { useDeleteConversation } from "@app/hooks/useDeleteConversation";
import { useHideTriggeredConversations } from "@app/hooks/useHideTriggeredConversations";
import {
  useBulkMoveConversationsToPod,
  useMoveConversationToPod,
} from "@app/hooks/useMoveConversationToPod";
import { useSendNotification } from "@app/hooks/useNotification";
import { CONVERSATIONS_UPDATED_EVENT } from "@app/lib/notifications/events";
import { useAppRouter, usePathParam } from "@app/lib/platform";
import { getSpaceIcon } from "@app/lib/spaces";
import { useActivationRecommendations } from "@app/lib/swr/activation";
import { useChats } from "@app/lib/swr/chats";
import { getConversationDotStatus } from "@app/lib/utils/conversation_dot_status";
import { getConversationRoute, getPodRoute } from "@app/lib/utils/router";
import { formatWakeUpSidebarLabel } from "@app/lib/utils/wakeup_description";
import type { GetBySpacesSummaryResponseBody } from "@app/types/api/assistant/conversation/spaces";
import type { ChatSummaryType } from "@app/types/api/chats";
import {
  type ConversationListItemType,
  getConversationDisplayTitle,
} from "@app/types/assistant/conversation";
import type { PodType, SpaceType } from "@app/types/space";
import type { WorkspaceType } from "@app/types/user";
import {
  ActionSparklesIcon,
  ArrowRight,
  Button,
  Checkbox,
  CheckDone01,
  Clock,
  cn,
  DotsHorizontal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Label,
  MessageAlertCircle,
  MessageTextSquare02,
  NavigationList,
  NavigationListCollapsibleSection,
  NavigationListItem,
  NavigationListItemAction,
  Plus,
  ScrollArea,
  Trash01,
  XClose,
  Zap,
  ZapOff,
} from "@dust-tt/sparkle";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// To avoid overwhelming the user with unread pod conversations, we hide them if there are too many.
const HIDE_UNREAD_POD_CONVERSATIONS_TRESHOLD = 16;
function conversationAttentionIcon(conversation: ConversationListItemType) {
  return getConversationDotStatus(conversation) === "blocked"
    ? MessageAlertCircle
    : MessageTextSquare02;
}

interface AgentSidebarMenuProps {
  owner: WorkspaceType;
}

export function AgentSidebarMenu({ owner }: AgentSidebarMenuProps) {
  const router = useAppRouter();
  const activeConversationId = useActiveConversationId();
  const bulkMoveConversationsToPod = useBulkMoveConversationsToPod(owner);
  const moveConversationToPod = useMoveConversationToPod(owner);

  const { setSidebarOpen } = useContext(SidebarContext);

  const { conversations, isConversationsError, mutateConversations } =
    useConversations({ workspaceId: owner.sId });

  const { summary, mutate: mutatePodConversationSummary } =
    usePodConversationsSummary({
      workspaceId: owner.sId,
    });

  const {
    pinned: pinnedChats,
    recent: recentChats,
    memoair: memoairChats,
    isChatsLoading,
  } = useChats({ owner });
  const activeChatId = usePathParam("chatId");

  useEffect(() => {
    const handleConversationsUpdated = () => {
      void mutateConversations();
      void mutatePodConversationSummary();
    };
    window.addEventListener(
      CONVERSATIONS_UPDATED_EVENT,
      handleConversationsUpdated
    );
    return () => {
      window.removeEventListener(
        CONVERSATIONS_UPDATED_EVENT,
        handleConversationsUpdated
      );
    };
  }, [mutateConversations, mutatePodConversationSummary]);

  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState<
    ConversationListItemType[]
  >([]);
  const doDelete = useDeleteConversation(owner);

  const { hideTriggeredConversations, setHideTriggeredConversations } =
    useHideTriggeredConversations();

  const [showDeleteDialog, setShowDeleteDialog] = useState<
    "all" | "selection" | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [titleFilter, setTitleFilter] = useState<string>("");
  const [isCreatePodModalOpen, setIsCreatePodModalOpen] = useState(false);
  const [pendingMoveToNewPod, setPendingMoveToNewPod] = useState(false);

  const sendNotification = useSendNotification();

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  const toggleMultiSelect = useCallback(() => {
    setIsMultiSelect((prev) => !prev);
    setSelectedConversations([]);
  }, [setIsMultiSelect, setSelectedConversations]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  const toggleConversationSelection = useCallback(
    (c: ConversationListItemType) => {
      if (selectedConversations.includes(c)) {
        setSelectedConversations((prev) => prev.filter((id) => id !== c));
      } else {
        setSelectedConversations((prev) => [...prev, c]);
      }
    },
    [selectedConversations, setSelectedConversations]
  );

  const deleteSelection = useCallback(async () => {
    setIsDeleting(true);
    const total = selectedConversations.length;
    let successCount = 0;
    if (total > 0) {
      for (const conversation of selectedConversations) {
        const ok = await doDelete(conversation);
        if (ok) {
          successCount += 1;
        }
      }
      toggleMultiSelect();
    }
    setIsDeleting(false);
    setShowDeleteDialog(null);
    if (!total) {
      return;
    }
    if (successCount === total) {
      sendNotification({
        type: "success",
        title: "Conversations successfully deleted",
        description: `${total} conversation${total > 1 ? "s" : ""} have been deleted.`,
      });
    } else if (successCount === 0) {
      sendNotification({
        type: "error",
        title: "Failed to delete conversations",
        description: `Could not delete the selected ${total > 1 ? "conversations" : "conversation"}.`,
      });
    } else {
      sendNotification({
        type: "error",
        title: "Some conversations couldn’t be deleted",
        description: `Deleted ${successCount} of ${total} conversations.`,
      });
    }
  }, [doDelete, selectedConversations, sendNotification, toggleMultiSelect]);

  const availablePods = useMemo(
    () => summary.map(({ space }) => space),
    [summary]
  );

  const moveSelectionToPod = useCallback(
    async (pod: PodType | SpaceType) => {
      setIsMoving(true);
      const successCount = await bulkMoveConversationsToPod(
        selectedConversations,
        pod
      );
      setIsMoving(false);
      if (successCount > 0) {
        toggleMultiSelect();
      }
      return successCount;
    },
    [bulkMoveConversationsToPod, selectedConversations, toggleMultiSelect]
  );

  const deleteAll = useCallback(async () => {
    setIsDeleting(true);
    const total = conversations.length;
    let successCount = 0;
    for (const conversation of conversations) {
      const ok = await doDelete(conversation);
      if (ok) {
        successCount += 1;
      }
    }
    if (!total) {
      return;
    }
    if (successCount === total) {
      sendNotification({
        type: "success",
        title: "Conversations successfully deleted",
        description: `${total} conversation${total > 1 ? "s" : ""} have been deleted.`,
      });
    } else if (successCount === 0) {
      sendNotification({
        type: "error",
        title: "Failed to delete conversations",
        description: "Could not delete conversation history.",
      });
    } else {
      sendNotification({
        type: "error",
        title: "Some conversations couldn’t be deleted",
        description: `Deleted ${successCount} of ${total} conversations.`,
      });
    }
    setIsDeleting(false);
    setShowDeleteDialog(null);
  }, [conversations, doDelete, sendNotification]);

  const { setShouldFocusInput } = useContext(InputBarContext);

  const handleNewClick = useCallback(async () => {
    setSidebarOpen(false);
    // Already on the new-conversation page: clicking "New" doesn't navigate, so
    // the input bar isn't remounted and mount-time autofocus won't run. Request
    // an explicit refocus instead. (activeConversationId is null on "new".)
    const isNewConversation =
      router.pathname.match(/^\/w\/[^/]+\/conversation\/[^/]+$/) !== null &&
      activeConversationId === null;
    if (isNewConversation) {
      setShouldFocusInput(true);
    }
  }, [setSidebarOpen, router, activeConversationId, setShouldFocusInput]);

  const { panelSection } = useNavigationPanelSection(owner);

  const allConversations = useMemo(() => {
    const unreadPodConversations = summary
      .map(({ unreadConversations }) => unreadConversations)
      .flat();
    if (
      unreadPodConversations.length >= HIDE_UNREAD_POD_CONVERSATIONS_TRESHOLD
    ) {
      return conversations;
    }
    return [...conversations, ...unreadPodConversations];
  }, [conversations, summary]);

  const hasTriggeredConversations = useMemo(
    () =>
      allConversations.some(
        (c: ConversationListItemType) => c.triggerId !== null
      ),
    [allConversations]
  );

  const filteredConversations = useMemo(() => {
    return filterReadTriggeredConversations(
      allConversations,
      hideTriggeredConversations
    );
  }, [allConversations, hideTriggeredConversations]);

  const sidebarTitleFilter = titleFilter;

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  const conversationsList = useMemo(() => {
    return (
      <ConversationsNavigationList
        conversations={filteredConversations}
        summary={summary}
        moveConversationToPod={moveConversationToPod}
        titleFilter={sidebarTitleFilter}
        isMultiSelect={isMultiSelect}
        selectedConversations={selectedConversations}
        toggleConversationSelection={toggleConversationSelection}
        activeConversationId={activeConversationId}
        owner={owner}
        hasTriggeredConversations={hasTriggeredConversations}
        hideTriggeredConversations={hideTriggeredConversations}
        setHideTriggeredConversations={setHideTriggeredConversations}
        handleNewClick={handleNewClick}
        toggleMultiSelect={toggleMultiSelect}
        setShowDeleteDialog={setShowDeleteDialog}
        pinnedChats={pinnedChats}
        recentChats={recentChats}
        memoairChats={memoairChats}
        isChatsLoading={isChatsLoading}
        activeChatId={activeChatId}
        panelSection={panelSection}
      />
    );
  }, [
    panelSection,
    filteredConversations,
    summary,
    moveConversationToPod,
    sidebarTitleFilter,
    isMultiSelect,
    selectedConversations,
    toggleConversationSelection,
    activeConversationId,
    owner,
    hasTriggeredConversations,
    hideTriggeredConversations,
    setHideTriggeredConversations,
    handleNewClick,
    toggleMultiSelect,
    setShowDeleteDialog,
    pinnedChats,
    recentChats,
    memoairChats,
    isChatsLoading,
    activeChatId,
  ]);

  return (
    <>
      <DeleteConversationsDialog
        isOpen={showDeleteDialog !== null}
        isDeleting={isDeleting}
        onClose={() => setShowDeleteDialog(null)}
        onDelete={showDeleteDialog === "all" ? deleteAll : deleteSelection}
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        type={showDeleteDialog || "all"}
        selectedCount={selectedConversations.length}
      />
      <CreatePodModal
        isOpen={isCreatePodModalOpen}
        onClose={() => {
          setIsCreatePodModalOpen(false);
          setPendingMoveToNewPod(false);
        }}
        onCreated={async (pod) => {
          setSidebarOpen(false);
          if (pendingMoveToNewPod) {
            setPendingMoveToNewPod(false);
            await moveSelectionToPod(pod);
          }
          void router.push(getPodRoute(owner.sId, pod.sId));
        }}
        owner={owner}
      />
      <div className="flex h-full min-h-0 flex-col">
        {isMultiSelect ? (
          <div className="z-50 flex shrink-0 justify-between gap-2 border-b border-border-dark/60 p-2 mb-4">
            <div className="flex gap-2">
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    label="Move to Project"
                    icon={ArrowRight}
                    disabled={selectedConversations.length === 0}
                    isLoading={isMoving}
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="max-w-60"
                  onFocusOutside={(e) => e.preventDefault()}
                >
                  <DropdownMenuItem
                    icon={Plus}
                    label="New Project"
                    onClick={() => {
                      setPendingMoveToNewPod(true);
                      setIsCreatePodModalOpen(true);
                    }}
                  />
                  {availablePods.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel label="Projects" />
                      {availablePods.map((pod) => (
                        <DropdownMenuItem
                          key={pod.sId}
                          icon={getSpaceIcon(pod)}
                          label={pod.name}
                          truncateText
                          onClick={() => moveSelectionToPod(pod)}
                        />
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant={
                  selectedConversations.length === 0 ? "outline" : "warning"
                }
                label="Delete"
                disabled={selectedConversations.length === 0}
                onClick={() => setShowDeleteDialog("selection")}
              />
            </div>
            <Button variant="ghost" icon={XClose} onClick={toggleMultiSelect} />
          </div>
        ) : (
          <div className="z-50 flex shrink-0 flex-col gap-2 p-sidebar-side-spacing empty:hidden">
            <SidebarSearch
              titleFilter={titleFilter}
              onTitleFilterChange={setTitleFilter}
            />
          </div>
        )}
        <div className="flex min-h-0 flex-1 flex-col">
          {isConversationsError && (
            <Label className="px-3 py-4 text-xs font-medium text-muted-foreground">
              Error loading conversations
            </Label>
          )}
          {conversationsList}
        </div>
      </div>
    </>
  );
}

const GRID_ANIMATE = { gridTemplateRows: "1fr", opacity: 1 };
const GRID_EXIT = { gridTemplateRows: "0fr", opacity: 0 };
const GRID_STYLE = { display: "grid" } as const;

interface WakeUpSuffixProps {
  nextWakeupAt: number;
}

function WakeUpSuffix({ nextWakeupAt }: WakeUpSuffixProps) {
  return (
    <span className="copy-xs flex items-center gap-1 text-muted-foreground">
      <Icon visual={Clock} size="xs" />
      {formatWakeUpSidebarLabel(nextWakeupAt)}
    </span>
  );
}

const ConversationListItem = memo(
  ({
    conversation,
    isMultiSelect,
    selectedConversations,
    toggleConversationSelection,
    activeConversationId,
    owner,
    showStatusDot = true,
  }: {
    conversation: ConversationListItemType;
    isMultiSelect: boolean;
    selectedConversations: ConversationListItemType[];
    toggleConversationSelection: (c: ConversationListItemType) => void;
    activeConversationId: string | null;
    owner: WorkspaceType;
    showStatusDot?: boolean;
  }) => {
    const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);
    const {
      isMenuOpen,
      menuTriggerPosition,
      handleRightClick,
      handleMenuOpenChange,
    } = useConversationMenu();

    const [showTypingAnimation, setShowTypingAnimation] = useState(false);
    const titleRef = useRef<string | null>(conversation.title); // Used to detect when the title changes to show the typing animation.

    useLayoutEffect(() => {
      if (titleRef.current === null && conversation.title !== null) {
        setShowTypingAnimation(true);
      }
      titleRef.current = conversation.title;
    }, [conversation.title]);

    const handleTypingAnimationComplete = useCallback(() => {
      setShowTypingAnimation(false);
    }, []);

    const conversationLabel = getConversationDisplayTitle(conversation);

    const handleDragStart = useCallback(
      (e: React.DragEvent) => {
        // Only allow dragging if not in multi-select mode and conversation is not already in a pod
        if (isMultiSelect || conversation.spaceId) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", conversation.sId);
        // Add a custom data type to identify conversation drags
        e.dataTransfer.setData(
          "application/x-dust-conversation",
          conversation.sId
        );
        // Store the full conversation object as JSON for the drop handler
        e.dataTransfer.setData(
          "application/json",
          JSON.stringify(conversation)
        );
      },
      [conversation, isMultiSelect]
    );

    return isMultiSelect ? (
      <div className="flex items-center mx-2 py-2">
        <Checkbox
          id={`conversation-${conversation.sId}`}
          className="bg-background"
          checked={selectedConversations.includes(conversation)}
          onCheckedChange={() => toggleConversationSelection(conversation)}
        />
        <Label
          htmlFor={`conversation-${conversation.sId}`}
          className="copy-sm ml-2 text-muted-foreground"
        >
          {conversationLabel}
        </Label>
      </div>
    ) : (
      <NavigationListItem
        key={conversation.sId}
        selected={activeConversationId === conversation.sId}
        label={conversationLabel}
        labelAnimation={
          showTypingAnimation
            ? "typing"
            : conversation.isRunningAgentLoop
              ? "streaming"
              : "none"
        }
        onTypingAnimationComplete={handleTypingAnimationComplete}
        href={getConversationRoute(owner.sId, conversation.sId)}
        shallow
        draggable={!conversation.spaceId}
        onDragStart={handleDragStart}
        className={cn(
          "memoair-conversation-row",
          !conversation.spaceId && "cursor-grab active:cursor-grabbing"
        )}
        icon={
          showStatusDot
            ? conversationAttentionIcon(conversation)
            : MessageTextSquare02
        }
        status="idle"
        hasActivity={conversation.unread}
        suffix={
          conversation.nextWakeupAt ? (
            <WakeUpSuffix nextWakeupAt={conversation.nextWakeupAt} />
          ) : undefined
        }
        moreMenu={
          <ConversationMenu
            activeConversationId={conversation.sId}
            conversation={conversation}
            owner={owner}
            trigger={() => <NavigationListItemAction />}
            isConversationDisplayed={activeConversationId === conversation.sId}
            isOpen={isMenuOpen}
            onOpenChange={handleMenuOpenChange}
            triggerPosition={menuTriggerPosition}
          />
        }
        onContextMenu={handleRightClick}
        onClick={async () => {
          // Side bar is the floating sidebar that appears when the screen is small.
          if (sidebarOpen) {
            setSidebarOpen(false);
            // Wait a bit before moving to the new conversation to avoid the sidebar from flickering.
            await new Promise((resolve) => setTimeout(resolve, 600));
          }
        }}
      />
    );
  }
);

interface ConversationsNavigationListProps {
  conversations: ConversationListItemType[];
  summary: GetBySpacesSummaryResponseBody["summary"];
  moveConversationToPod: (
    conversation: ConversationListItemType,
    space: SpaceType
  ) => Promise<boolean>;
  titleFilter: string;
  isMultiSelect: boolean;
  selectedConversations: ConversationListItemType[];
  toggleConversationSelection: (conversation: ConversationListItemType) => void;
  activeConversationId: string | null;
  owner: WorkspaceType;
  hasTriggeredConversations: boolean;
  hideTriggeredConversations: boolean;
  setHideTriggeredConversations: (hide: boolean) => void;
  handleNewClick: () => void;
  toggleMultiSelect: () => void;
  setShowDeleteDialog: (value: "all" | "selection" | null) => void;
  pinnedChats: ChatSummaryType[];
  recentChats: ChatSummaryType[];
  memoairChats: ChatSummaryType[];
  isChatsLoading: boolean;
  activeChatId: string | null;
  panelSection: NavigationPanelSection;
}

function ConversationsNavigationList({
  conversations,
  summary,
  moveConversationToPod,
  titleFilter,
  isMultiSelect,
  selectedConversations,
  toggleConversationSelection,
  activeConversationId,
  owner,
  hasTriggeredConversations,
  hideTriggeredConversations,
  setHideTriggeredConversations,
  handleNewClick,
  toggleMultiSelect,
  setShowDeleteDialog,
  pinnedChats,
  recentChats,
  memoairChats,
  isChatsLoading,
  activeChatId,
  panelSection,
}: ConversationsNavigationListProps) {
  const reduceMotion = useReducedMotion();
  // Conversations opened from an activation recommendation are pinned into
  // their own highlighted "Recommendations for you" section at the top and
  // pulled out of the other sections so they only appear once. The recs are
  // fetched once (SWR dedupes) and carry the conversation they opened.
  const { recommendations: activationRecommendations } =
    useActivationRecommendations({ workspaceId: owner.sId });
  const activationConversationIds = useMemo(
    () =>
      new Set(
        activationRecommendations
          .map((rec) => rec.conversationId)
          .filter((id): id is string => id !== null)
      ),
    [activationRecommendations]
  );
  const activationConversations = useMemo(
    () => conversations.filter((c) => activationConversationIds.has(c.sId)),
    [conversations, activationConversationIds]
  );

  const projectsContent = (
    <div className="flex flex-col gap-0.5">
      {renderPodsList({
        owner,
        summary: orderSidebarProjects(summary, ""),
        titleFilter,
        memoairChats,
        activeChatId,
        moveConversationToPod,
      })}
    </div>
  );

  return (
    <ScrollArea className="dd-privacy-mask h-full w-full">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={panelSection}
          className="flex flex-col gap-4 pt-2"
          initial={reduceMotion ? false : { opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, x: 6 }}
          transition={{ duration: 0.16, ease: "easeOut" }}
        >
          {panelSection === "dms" ? (
            <>
              <AnimatePresence initial={false}>
                {activationConversations.length > 0 && (
                  <motion.div
                    key="recommendations"
                    style={GRID_STYLE}
                    animate={GRID_ANIMATE}
                    exit={GRID_EXIT}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <div className="overflow-hidden">
                      <NavigationListCollapsibleSection
                        label="Recommendations for you"
                        icon={ActionSparklesIcon}
                        count={activationConversations.length}
                        className="bg-highlight-50 rounded-xl border border-highlight-200 p-1 mx-sidebar-side-spacing"
                      >
                        {activationConversations.map((conversation) => (
                          <motion.div
                            key={conversation.sId}
                            style={GRID_STYLE}
                            animate={GRID_ANIMATE}
                            exit={GRID_EXIT}
                            transition={{ ease: "easeOut", duration: 0.1 }}
                          >
                            <div className="overflow-hidden">
                              <ConversationListItem
                                conversation={conversation}
                                isMultiSelect={isMultiSelect}
                                selectedConversations={selectedConversations}
                                toggleConversationSelection={
                                  toggleConversationSelection
                                }
                                activeConversationId={activeConversationId}
                                owner={owner}
                                showStatusDot={false}
                              />
                            </div>
                          </motion.div>
                        ))}
                      </NavigationListCollapsibleSection>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <NavigationList className="mx-sidebar-side-spacing">
                <div className="flex flex-col gap-1">
                  <div className="flex h-8 items-center gap-1.5 px-2">
                    <span className="flex-1 truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Chats
                    </span>
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="xmini"
                          icon={DotsHorizontal}
                          variant="ghost"
                          aria-label="Conversations options"
                        />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        onFocusOutside={(e) => e.preventDefault()}
                      >
                        <DropdownMenuLabel label="Conversations" />
                        <DropdownMenuItem
                          label={
                            hideTriggeredConversations
                              ? "Show triggered"
                              : "Hide triggered"
                          }
                          icon={hideTriggeredConversations ? Zap : ZapOff}
                          disabled={!hasTriggeredConversations}
                          onClick={() =>
                            setHideTriggeredConversations(
                              !hideTriggeredConversations
                            )
                          }
                        />
                        <DropdownMenuItem
                          label="Edit history"
                          icon={CheckDone01}
                          onClick={toggleMultiSelect}
                          disabled={conversations.length === 0}
                        />
                        <DropdownMenuItem
                          label="Clear history"
                          variant="warning"
                          icon={Trash01}
                          onClick={() => setShowDeleteDialog("all")}
                          disabled={conversations.length === 0}
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <ChatsList
                      owner={owner}
                      pinned={pinnedChats}
                      recent={recentChats}
                      isLoading={isChatsLoading}
                      titleFilter={titleFilter}
                      activeChatId={activeChatId}
                    />
                  </div>
                </div>
              </NavigationList>
            </>
          ) : panelSection === "contacts" ? (
            <ContactsList owner={owner} titleFilter={titleFilter} />
          ) : (
            <NavigationList className="mx-sidebar-side-spacing">
              {projectsContent}
            </NavigationList>
          )}
        </motion.div>
      </AnimatePresence>
    </ScrollArea>
  );
}
